import { ApiQueryParams, DefaultQueryParams, PaginatedResponse } from '@frontend/api-utils';
import { Permission } from '@frontend/authorization';
import { ClassType, EntityType, Locale } from '@frontend/common';
import { HorizontalButtonGroupButton } from '@frontend/components/common';
import { ConfirmationModal } from '@frontend/components/modal';
import { TablePlaceholder } from '@frontend/elements';
import { Report } from '@frontend/report/types';
import { DefaultListValues, resetSelect } from '@frontend/repository';
import { Action, ThunkDispatch } from '@reduxjs/toolkit';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useLocation } from 'react-router-dom';

import { GroupedList, GroupedListColumn, GroupedListGroup } from '../grouped-list/grouped-list';
import { FilterOptions, HeaderFilter } from '../header/header-filter';
import { InsaneFilter } from '../insane-filter/insane-filter.component';
import { ObjectColumnsProps } from '../object-column-mapper';
import { useObjectListLayout } from '../object-list-layout/object-list-layout.context';
import { ObjectListActionButtons } from '../object-list/object-list-action-buttons';
import { ObjectListLoadingTable } from '../object-list/object-list-loading-table';
import { MultiSelectOptions } from '../object-list/object-list.component';
import useObjectList from '../object-list/object-list.controller';

export interface GroupedObjectListProps<T extends { id: string }, TGroup = any> {
    listStoreIdCallback?: (id: string) => void;
    label?: React.ReactNode;
    fetch: (arg?: ApiQueryParams<DefaultQueryParams | string | number>) => Promise<PaginatedResponse<T>>;
    dispatch: ThunkDispatch<any, any, Action>;
    eventListener?: EntityType;
    id?: string;
    /**
     * @default false
     * @description set to true if the list should be taken out of the cache (only works when passing a unique id)
     */
    useStaticListId?: boolean;
    columns: Omit<ObjectColumnsProps<T>, 'dispatch'>;
    multiSelectOptions?: MultiSelectOptions<T>;
    onSelectElement?: (object: T) => void;
    sortingColumns?: string[];
    create?: {
        label?: React.ReactNode;
        onCreate: () => void;
        requiredPermissions?: Permission[];
        overwriteCreateLabel?: React.ReactNode;
    };
    includeDeleted?: {
        requiredPermissions: Permission[];
    };
    type: EntityType;
    filter?: FilterOptions;
    activeFilter?: {
        searchKeys?: string[];
        filterKeys?: string[];
        extrtaProps?: Map<string, object>;
        displayName?: Map<string, React.ReactNode>;
        queryKeyMap?: Map<string, string>;
        export?: {
            overwriteExport?: () => void;
            modalId?: string;
            report?: Report;
            data?: { [key: string]: any };
            dataKeys?: string[];
        };
    };
    selectedObjects?: (obj: T[]) => void;
    defaultListValues?: DefaultListValues;

    cardClassName?: string;
    cardHeaderClassName?: string;
    cardBodyClassName?: string;
    rowClassName?: string;
    customDivclassName?: string;
    customLabelClassName?: string;
    customButtons?: HorizontalButtonGroupButton[];
    customHeaderComponent?: React.ReactNode;
    hideColumns?: string[];

    showCount?: boolean;
    locale?: Locale;

    // Grouped list specific props
    groupedListProps: {
        /** Function to extract groups from the data */
        groupBy: (data: T[]) => GroupedListGroup<T, TGroup>[];
        /** Render function for group header content */
        renderGroupHeader: (group: TGroup) => React.ReactNode;
        /** Optional render function for group actions */
        renderGroupActions?: (group: TGroup) => React.ReactNode;
        /** Optional className for the header row */
        headerClassName?: string;
        /** Optional className for group header rows */
        groupHeaderClassName?: string;
        /** Optional className for data rows */
        dataRowClassName?: string;
    };
}

export const GroupedObjectList = <T extends { id: string }, TGroup = any>(props: GroupedObjectListProps<T, TGroup>) => {
    const viewProps = useObjectList<T>(props);
    const location = useLocation();
    const listLayout = useObjectListLayout();
    const isInitialLoading = viewProps.isLoading && viewProps.data === null;
    const usesAdminLayout = listLayout === 'admin' || location.pathname.includes('/admin');

    // Transform data into grouped structure
    const groups = React.useMemo(() => {
        if (!viewProps.data) return [];
        return props.groupedListProps.groupBy(viewProps.data);
    }, [viewProps.data, props.groupedListProps.groupBy]);

    // Convert TanStack Table columns to GroupedList columns
    const groupedListColumns = React.useMemo<GroupedListColumn<T>[]>(() => {
        return viewProps.columns
            .filter((col) => col.id && col.id !== 'select' && col.id !== 'expand')
            .map((col) => ({
                key: col.id!,
                header: typeof col.header === 'string' ? col.header : col.id!,
                render: col.cell
                    ? (item: T) => {
                          const cell = col.cell as any;
                          // Create a minimal cell context for rendering
                          const context = {
                              getValue: () => (item as any)[col.id!],
                              row: { original: item },
                              column: col,
                              cell: { getValue: () => (item as any)[col.id!] },
                              table: {} as any
                          };
                          return cell(context);
                      }
                    : undefined,
                accessor: (item: T) => (item as any)[col.id!]
            }));
    }, [viewProps.columns]);

    return (
        <>
            <div
                id={props.id}
                className={
                    'bg-background-surface border-border-default rounded-xl border ' + (props.cardClassName !== undefined ? props.cardClassName : 'w-full')
                }>
                {usesAdminLayout ? (
                    <div
                        id='admin-table-header'
                        className={props.cardHeaderClassName !== undefined ? props.cardHeaderClassName : 'inline-flex w-full items-center justify-between p-4'}>
                        <div className='flex w-full flex-col items-start md:flex-row md:content-start md:items-center md:justify-between'>
                            <div className='size- order-1 mb-2 inline-flex items-center justify-start gap-3 md:mb-0 md:w-1/3'>
                                {props.filter && (
                                    <HeaderFilter<T>
                                        dispatch={props.dispatch}
                                        filter={props.filter}
                                        {...viewProps}
                                    />
                                )}
                            </div>
                            <h4 className='text-brand-primary order-1 m-0 ms-2 mb-2 text-2xl font-semibold md:order-2 md:mb-0 md:w-1/3 md:text-center'>
                                {props.label}
                            </h4>
                            {props.customHeaderComponent && props.customHeaderComponent}
                            <div className='order-2 mb-2 md:order-3 md:mb-0 md:w-1/3'>
                                <ObjectListActionButtons<T>
                                    {...props}
                                    {...viewProps}
                                    onMultiDelete={() =>
                                        viewProps.openModal(viewProps.multiDeleteModalId, () => (
                                            <ConfirmationModal
                                                handleClose={() => viewProps.closeModal(viewProps.multiDeleteModalId)}
                                                message={
                                                    props.multiSelectOptions?.delete?.parent
                                                        ? parentMultiDeleteMessage(
                                                              viewProps.selectedObjects.length,
                                                              props.type,
                                                              props.multiSelectOptions.delete.parent
                                                          )
                                                        : listMultiDeleteMessage(viewProps.selectedObjects.length, props.type)
                                                }
                                                onConfirm={() => {
                                                    props
                                                        .multiSelectOptions!.delete!.delete(viewProps.selectedObjects.map((o) => o.id))
                                                        .then(() => {
                                                            if (viewProps.listId) {
                                                                props.dispatch(resetSelect({ listId: viewProps.listId }));
                                                            }
                                                            viewProps.changeSelectedObjects([]);
                                                        })
                                                        .then(() => viewProps.closeModal(viewProps.multiDeleteModalId));
                                                }}
                                                severity={ClassType.DANGER}
                                                show={true}
                                                cascadeWarning
                                            />
                                        ))
                                    }
                                />
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className='flex flex-col'>
                        {props.label && (
                            <div
                                className={
                                    props.customLabelClassName
                                        ? props.customLabelClassName
                                        : 'inline-flex items-start justify-start gap-2.5 self-stretch px-4 pt-4'
                                }>
                                <div className='text-foreground-default justify-start text-xl font-semibold'>{props.label}</div>
                            </div>
                        )}
                        {props.activeFilter && (
                            <InsaneFilter
                                dispatch={props.dispatch}
                                listId={viewProps.listId}
                                {...props.activeFilter}
                            />
                        )}
                    </div>
                )}
                <div className='card-body mx-0 mb-0 px-0 pt-0 pb-0'>
                    {isInitialLoading ? (
                        <ObjectListLoadingTable columns={viewProps.columns.length} />
                    ) : viewProps.data ? (
                        <GroupedList<T, TGroup>
                            groups={groups}
                            columns={groupedListColumns}
                            renderGroupHeader={props.groupedListProps.renderGroupHeader}
                            renderGroupActions={props.groupedListProps.renderGroupActions}
                            onRowClick={props.onSelectElement}
                            headerClassName={props.groupedListProps.headerClassName}
                            groupHeaderClassName={props.groupedListProps.groupHeaderClassName}
                            dataRowClassName={props.groupedListProps.dataRowClassName}
                        />
                    ) : (
                        <TablePlaceholder />
                    )}
                </div>
            </div>
        </>
    );
};

const listMultiDeleteMessage = (count: number, type: EntityType) => {
    return (
        <FormattedMessage
            id='ObjectList.multi-delete-modal.message'
            defaultMessage='Are you sure you want to remove {count} {type}s?'
            values={{ count, type: type.replace(/_/g, ' ') }}
        />
    );
};

const parentMultiDeleteMessage = (count: number, type: EntityType, parent: React.ReactNode) => {
    return (
        <FormattedMessage
            id='ObjectList.multi-delete-modal.message_with_parent'
            defaultMessage='Are you sure you want to remove {count} {type}s from this {parent}?'
            values={{ count, type: type.replace(/_/g, ' '), parent }}
        />
    );
};
